// src/pages/VehicleV2/AddNewTyre.jsx

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaTimes, FaUpload, FaTrash, FaArrowLeft } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/addNewTyre";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const ddmmyyyyToISO = (s) => {
  if (!s) return "";
  const txt = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(txt)) return txt;
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(txt)) return "";
  const [dd, mm, yyyy] = txt.split("/");
  return `${yyyy}-${mm}-${dd}`;
};

const AddNewTyre = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const tyreId = useMemo(() => toInt(searchParams.get("tyreId")), [searchParams]);
  const isEdit = tyreId > 0;

  const loginUserId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const branchId = Number(
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || 8
  );

  const finYear =
    localStorage.getItem("financialYear") ||
    localStorage.getItem("finyear") ||
    localStorage.getItem("finYear") ||
    "";
  const finYearId =
    localStorage.getItem("financialYearId") ||
    localStorage.getItem("finyearid") ||
    localStorage.getItem("finYearId") ||
    localStorage.getItem("financialyearid") ||
    "";

  const [addTyreVI, setAddTyreVI] = useState(0);
  const [locations, setLocations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [purchaseFromList, setPurchaseFromList] = useState([]);
  const [positions, setPositions] = useState([]);
  const [serialOptions, setSerialOptions] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    typeSizePlyRating: "",
    typeMake: "",
    purchaseDate: "",
    typeAmount: "",
    locationId: branchId ? String(branchId) : "",
    vehicleId: "",
    typeSerialNo: "",
    storeIssueDetId: "",
    storeIssueSerialText: "",
    position: "",
    typeModel: "",
    purchaseFrom: "",
    purchaseFromNew: "",
  });

  const inventoryHeaders = useMemo(() => {
    const headers = { "x-branchid": branchId, "x-userid": loginUserId };
    if (toStr(finYear)) headers["x-finyear"] = toStr(finYear);
    if (toStr(finYearId)) {
      headers["x-finyearid"] = toStr(finYearId);
      headers["x-finyear-id"] = toStr(finYearId);
    }
    return headers;
  }, [finYear, finYearId, branchId, loginUserId]);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  useEffect(() => {
    setFormData((p) => ({ ...p, locationId: branchId ? String(branchId) : "" }));
  }, [branchId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "locationId") return;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);

        const cfg = await axios.get(`${API_BASE}/config`, { headers: inventoryHeaders });
        const vi = toInt(cfg.data?.addTyreVI, 0);
        setAddTyreVI(vi);

        const dd = await axios.get(`${API_BASE}/dropdowns`, {
          params: { branchId, branchType, addTyreVI: vi },
          headers: inventoryHeaders,
        });

        setLocations(dd.data?.locations || []);
        setVehicles(dd.data?.vehicles || []);
        setPurchaseFromList(dd.data?.purchaseFrom || []);
        setPositions(dd.data?.positions || []);

        setFormData((p) => ({ ...p, locationId: branchId ? String(branchId) : "" }));

        if (tyreId > 0) {
          const v = await axios.get(`${API_BASE}/view`, {
            params: { tyreId },
            headers: inventoryHeaders,
          });
          const t = v.data?.tyre;
          const docsResp = v.data?.documents || [];

          if (t) {
            setDocs(docsResp.map((d) => d.file).filter(Boolean));
            setFormData((p) => ({
              ...p,
              date: ddmmyyyyToISO(t.datereceipt) || p.date,
              typeSizePlyRating: t.fld_Tyresize || "",
              typeMake: t.fld_TyreMake || "",
              purchaseDate: ddmmyyyyToISO(t.Purchasedate) || "",
              typeAmount: t.fld_TyreAmounts || "",
              locationId: branchId ? String(branchId) : "",
              vehicleId: String(t.fld_vehicleid ?? ""),
              position: t.fld_TyrePosition || "",
              typeModel: t.fld_TyreModel || "",
              purchaseFrom: t.fld_Purchasefrom || "",
              typeSerialNo: t.fld_Tyreserialno || "",
              storeIssueSerialText: t.fld_Tyreserialno || "",
            }));
          }
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [tyreId, branchId, branchType, inventoryHeaders]);

  useEffect(() => {
    const loadSerials = async () => {
      try {
        if (addTyreVI !== 1) return;
        const vehId = toInt(formData.vehicleId);
        if (!vehId) {
          setSerialOptions([]);
          setFormData((p) => ({
            ...p,
            storeIssueDetId: "",
            storeIssueSerialText: "",
          }));
          return;
        }

        const rs = await axios.get(`${API_BASE}/tyre-serials`, {
          params: { vehicleId: vehId, branchId },
          headers: inventoryHeaders,
        });
        setSerialOptions(rs.data?.serials || []);
        setFormData((p) => ({
          ...p,
          storeIssueDetId: "",
          storeIssueSerialText: "",
          typeMake: "",
          typeSizePlyRating: "",
          typeAmount: "",
          purchaseDate: "",
          purchaseFrom: "",
          purchaseFromNew: "",
        }));
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Failed to load serials");
      }
    };

    loadSerials();
  }, [formData.vehicleId, addTyreVI, branchId, inventoryHeaders]);

  useEffect(() => {
    const loadTyreDetail = async () => {
      try {
        if (addTyreVI !== 1) return;
        const sid = toInt(formData.storeIssueDetId);
        const serialText = formData.storeIssueSerialText;
        if (!sid || !serialText) return;

        const rs = await axios.get(`${API_BASE}/tyre-detail`, {
          params: { storeIssueDetId: sid, serialNo: serialText },
          headers: inventoryHeaders,
        });
        const d = rs.data?.detail;
        if (d) {
          setFormData((p) => ({
            ...p,
            typeMake: d.make || "",
            typeSizePlyRating: d.partNo || "",
            typeAmount: d.price || "",
            purchaseFrom: d.supplierName || "",
            purchaseDate: ddmmyyyyToISO(d.issueDate) || "",
          }));
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Failed to load tyre detail");
      }
    };

    loadTyreDetail();
  }, [formData.storeIssueDetId, formData.storeIssueSerialText, addTyreVI, inventoryHeaders]);

  const handleFileUpload = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("document", file);
      const rs = await axios.post(`${API_BASE}/upload`, fd, {
        headers: { ...inventoryHeaders, "Content-Type": "multipart/form-data" },
      });
      const filename = rs.data?.filename;
      if (filename) {
        setDocs((p) => [...p, filename]);
        toast.success("File uploaded");
      } else {
        toast.error("Upload failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const removeDoc = (idx) => setDocs((p) => p.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.date) return toast.error("Date is required");
    if (!formData.typeSizePlyRating) return toast.error("Type Size/Ply Rating is required");
    if (!formData.typeMake) return toast.error("Type Make is required");
    if (!formData.purchaseDate) return toast.error("Purchase Date is required");
    if (!formData.vehicleId) return toast.error("Vehicle Code is required");
    if (!formData.position) return toast.error("Position is required");

    let serialNoToSend = "";
    let storeIssueDetIdToSend = 0;

    if (addTyreVI === 1) {
      if (!formData.storeIssueDetId || !formData.storeIssueSerialText) {
        return toast.error("Type Serial No is required");
      }
      serialNoToSend = formData.storeIssueSerialText;
      storeIssueDetIdToSend = toInt(formData.storeIssueDetId);
    } else {
      if (!formData.typeSerialNo) return toast.error("Type Serial No is required");
      serialNoToSend = formData.typeSerialNo;
    }

    const purchaseFromMode =
      String(formData.purchaseFrom || "").trim() === "New" ? "NEW" : "EXISTING";

    const payload = {
      tyreId: isEdit ? tyreId : 0,
      branchId,
      branchType,
      addTyreVI,
      locationId: toInt(branchId),
      date: formData.date,
      vehicleId: toInt(formData.vehicleId),
      tyreSerialNo: serialNoToSend,
      storeIssueDetId: storeIssueDetIdToSend,
      tyreSize: formData.typeSizePlyRating,
      position: formData.position,
      tyreMake: formData.typeMake,
      tyreModel: formData.typeModel,
      purchaseDate: formData.purchaseDate,
      purchaseFromMode,
      purchaseFrom: formData.purchaseFrom,
      purchaseFromNew: formData.purchaseFromNew,
      amount: formData.typeAmount,
      loginUserId,
      ipAddress: "",
      documents: docs,
    };

    try {
      setLoading(true);
      const rs = await axios.post(`${API_BASE}/save`, payload, { headers: inventoryHeaders });
      if (rs.data?.success) {
        toast.success(rs.data?.message || "Saved");
        navigate("/vehicle/TyreDetailsHome");
      } else {
        toast.error(rs.data?.message || "Save failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setDocs([]);
    setSerialOptions([]);
    setFormData({
      date: new Date().toISOString().split("T")[0],
      typeSizePlyRating: "",
      typeMake: "",
      purchaseDate: "",
      typeAmount: "",
      locationId: branchId ? String(branchId) : "",
      vehicleId: "",
      typeSerialNo: "",
      storeIssueDetId: "",
      storeIssueSerialText: "",
      position: "",
      typeModel: "",
      purchaseFrom: "",
      purchaseFromNew: "",
    });
    toast.info("Form cleared");
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ADD NEW TYRE</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <form onSubmit={handleSubmit}>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <div>
                <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {isEdit ? "Edit New Tyre" : "Add New Tyre"}
                </h2>
                {loading && (
                  <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Loading...
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  <FaArrowLeft /> Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  } ${buttonClass} ${hoverClass}`}
                >
                  <FaSave />
                  {loading ? "Saving..." : isEdit ? "Update" : "Save"}
                </button>
              </div>
            </div>

            <Section title="Tyre Details" isDark={isDark}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Field label="Date *" labelClass={labelClass}>
                  <input type="date" name="date" value={formData.date} onChange={handleInputChange} className={inputClass} />
                </Field>

                <Field label="Location *" labelClass={labelClass}>
                  <select
                    name="locationId"
                    value={formData.locationId}
                    className={`${inputClass} opacity-80 cursor-not-allowed`}
                    disabled
                    title="Location locked to your branch"
                  >
                    <option value="">Select</option>
                    {locations.map((b) => (
                      <option key={b.id} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Vehicle Code *" labelClass={labelClass}>
                  <select
                    name="vehicleId"
                    value={formData.vehicleId}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={String(v.id)}>
                        {v.code}
                      </option>
                    ))}
                  </select>
                </Field>

                {addTyreVI === 1 ? (
                  <Field label="Type Serial No *" labelClass={labelClass}>
                    <select
                      name="storeIssueDetId"
                      value={formData.storeIssueDetId}
                      onChange={(e) => {
                        const val = e.target.value;
                        const opt = serialOptions.find((x) => String(x.id) === String(val));
                        setFormData((p) => ({
                          ...p,
                          storeIssueDetId: val,
                          storeIssueSerialText: opt?.text || "",
                        }));
                      }}
                      className={inputClass}
                    >
                      <option value="">Select</option>
                      {serialOptions.map((s) => (
                        <option key={s.id} value={String(s.id)}>
                          {s.text}
                        </option>
                      ))}
                    </select>
                    {isEdit && formData.storeIssueSerialText && (
                      <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        Current Serial: {formData.storeIssueSerialText}
                      </p>
                    )}
                  </Field>
                ) : (
                  <Field label="Type Serial No *" labelClass={labelClass}>
                    <input
                      type="text"
                      name="typeSerialNo"
                      value={formData.typeSerialNo}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="Enter serial number"
                    />
                  </Field>
                )}

                <Field label="Type Size / Ply Rating *" labelClass={labelClass}>
                  <input
                    type="text"
                    name="typeSizePlyRating"
                    value={formData.typeSizePlyRating}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter type size/ply rating"
                  />
                </Field>

                <Field label="Position *" labelClass={labelClass}>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {positions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Type Make *" labelClass={labelClass}>
                  <input
                    type="text"
                    name="typeMake"
                    value={formData.typeMake}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter tyre make"
                  />
                </Field>

                <Field label="Type Model" labelClass={labelClass}>
                  <input
                    type="text"
                    name="typeModel"
                    value={formData.typeModel}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter tyre model"
                  />
                </Field>
              </div>
            </Section>

            <Section title="Purchase Details" isDark={isDark}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Field label="Purchase Date *" labelClass={labelClass}>
                  <input
                    type="date"
                    name="purchaseDate"
                    value={formData.purchaseDate}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </Field>

                <Field label="Type Amount *" labelClass={labelClass}>
                  <input
                    type="number"
                    name="typeAmount"
                    value={formData.typeAmount}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter amount"
                  />
                </Field>

                <Field label="Purchase From *" labelClass={labelClass}>
                  <select
                    name="purchaseFrom"
                    value={formData.purchaseFrom}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((p) => ({
                        ...p,
                        purchaseFrom: val,
                        purchaseFromNew: val === "New" ? p.purchaseFromNew : "",
                      }));
                    }}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {purchaseFromList.map((p) => (
                      <option key={p.id ?? p.name} value={p.name}>
                        {p.name}
                      </option>
                    ))}
                    <option value="New">New</option>
                  </select>
                </Field>

                {formData.purchaseFrom === "New" && (
                  <Field label="New Purchase From" labelClass={labelClass}>
                    <input
                      type="text"
                      name="purchaseFromNew"
                      value={formData.purchaseFromNew}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="Enter new supplier"
                    />
                  </Field>
                )}
              </div>
            </Section>

            <Section title="Documents" isDark={isDark}>
              <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer border ${
                isDark
                  ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                  : "border-gray-300 text-gray-700 hover:bg-white"
              }`}>
                <FaUpload />
                Choose File
                <input type="file" onChange={handleFileUpload} className="hidden" />
              </label>

              {docs.length > 0 && (
                <div className="mt-4 space-y-2">
                  {docs.map((d, idx) => (
                    <div
                      key={`${d}-${idx}`}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                        isDark ? "border-gray-700 bg-gray-800/40" : "border-gray-200 bg-white"
                      }`}
                    >
                      <a
                        href={`https://nissi-india.com/InvoiceDetails/${encodeURIComponent(d)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm underline"
                      >
                        {d}
                      </a>
                      <button
                        type="button"
                        onClick={() => removeDoc(idx)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <div className="flex justify-between items-center mt-2 pt-6 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
              <button
                type="button"
                onClick={handleReset}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <FaTimes /> Reset
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass}`}
              >
                <FaSave />
                {isEdit ? "Update Tyre Details" : "Save Tyre Details"}
              </button>
            </div>
          </form>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

function Section({ title, isDark, children }) {
  return (
    <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, labelClass, children }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

export default AddNewTyre;